import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Latex from "react-latex-next";
import { useState } from "react";
import {
  anthropomorphicArmLinkList,
  anthropomorphicArmSphericalWristLinkList,
  dlrManipulatorLinkList,
  sphericalArmLinkList,
  sphericalWristLinkList,
  stanfordManipulatorLinkList,
  threeLinkPlanarArmLinkList,
} from "./armTemplate";
import NextAction from "../../utils/NextAction";

export default function Controls({ controls }) {
  const { linkList, setLinkList, setStartAnimation, endEffectorPos } = controls;
  const [age, setAge] = useState("");

  const handleChange = (event: SelectChangeEvent) => {
    setAge(event.target.value);
    if (event.target.value == "threeLinkPlanarArm") {
      setLinkList(threeLinkPlanarArmLinkList);
    } else if (event.target.value == "sphericalArm") {
      setLinkList(sphericalArmLinkList);
    } else if (event.target.value == "anthropomorphicArm") {
      setLinkList(anthropomorphicArmLinkList);
    } else if (event.target.value == "sphericalWrist") {
      setLinkList(sphericalWristLinkList);
    } else if (event.target.value == "stanfordManipulator") {
      setLinkList(stanfordManipulatorLinkList);
    } else if (event.target.value == "anthropomorphicArmSphericalWrist") {
      setLinkList(anthropomorphicArmSphericalWristLinkList);
    } else if (event.target.value == "dlrManipulator") {
      setLinkList(dlrManipulatorLinkList);
    }
  };

  return (
    <div className="controls">
      <div className="controlGroup">
        <Typography variant="h5" style={{ paddingBottom: 20 }}>
          Denavit–Hartenberg Parameters <br />
          (Classic DH Parameters)
        </Typography>
        <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
          Please note that there are two versions of DH parameters: classic and
          modified. We talk about the <b>classic DH parameters</b> in this page.
          You can see{" "}
          <a href={"/modified-dh-model"}>the modified DH parameters</a> here.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Here is the table of classic DH parameters. Feel free to try.
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            maxWidth: 450,
            marginBottom: 1,
            marginRight: "auto",
            marginLeft: "auto",
          }}
        >
          <Table sx={{ minWidth: 350 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell style={{ maxWidth: 30, padding: 5 }} align="center">
                  Link
                </TableCell>
                <TableCell
                  align="right"
                  style={{ padding: 5, textAlign: "center" }}
                >
                  <Latex>$d_n$</Latex>
                </TableCell>
                <TableCell
                  align="right"
                  style={{ padding: 5, textAlign: "center" }}
                >
                  <Latex>$\theta_n$</Latex>
                </TableCell>
                <TableCell
                  align="right"
                  style={{ padding: 5, textAlign: "center" }}
                >
                  <Latex>$a_n$</Latex>
                </TableCell>
                <TableCell
                  align="right"
                  style={{ padding: 5, textAlign: "center" }}
                >
                  <Latex>$\alpha_n$</Latex>
                </TableCell>
                <TableCell
                  align="right"
                  style={{ padding: 5, textAlign: "center" }}
                >
                  <Latex>type</Latex>
                </TableCell>
                <TableCell
                  align="right"
                  style={{ padding: 5, textAlign: "center" }}
                >
                  <Latex>axis</Latex>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linkList.map((link, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    align="center"
                    style={{ padding: 10 }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell align="right" style={{ padding: 5 }}>
                    <input
                      style={{ width: 40 }}
                      value={link.d}
                      onChange={(e) => {
                        const newLink = { ...link };
                        const value = e.target.value;
                        newLink.d =
                          value == "" || value == "-"
                            ? value
                            : parseInt(e.target.value);
                        const newLinkList = [...linkList];
                        newLinkList[index] = newLink;
                        setLinkList(newLinkList);
                      }}
                      step={1}
                      type="number"
                    />
                  </TableCell>
                  <TableCell align="right" style={{ padding: 5 }}>
                    <input
                      style={{ width: 40 }}
                      value={link.theta}
                      onChange={(e) => {
                        const newLink = { ...link };
                        const value = e.target.value;
                        newLink.theta =
                          value == "" || value == "-"
                            ? value
                            : parseInt(e.target.value);
                        const newLinkList = [...linkList];
                        newLinkList[index] = newLink;
                        setLinkList(newLinkList);
                      }}
                      step={1}
                      type="number"
                    />
                  </TableCell>
                  <TableCell align="right" style={{ padding: 5 }}>
                    <input
                      style={{ width: 40 }}
                      value={link.a}
                      onChange={(e) => {
                        const newLink = { ...link };
                        const value = e.target.value;
                        newLink.a =
                          value == "" || value == "-"
                            ? value
                            : parseInt(e.target.value);
                        const newLinkList = [...linkList];
                        newLinkList[index] = newLink;
                        setLinkList(newLinkList);
                      }}
                      step={1}
                      type="number"
                    />
                  </TableCell>
                  <TableCell align="right" style={{ padding: 5 }}>
                    <input
                      style={{ width: 40 }}
                      value={link.alpha}
                      onChange={(e) => {
                        const newLink = { ...link };
                        const value = e.target.value;
                        newLink.alpha =
                          value == "" || value == "-"
                            ? value
                            : parseInt(e.target.value);
                        const newLinkList = [...linkList];
                        newLinkList[index] = newLink;
                        setLinkList(newLinkList);
                      }}
                      step={1}
                      type="number"
                    />
                  </TableCell>
                  <TableCell align="right" style={{ padding: 0 }}>
                    <Select
                      labelId="demo-select-small-label"
                      id="demo-select-small"
                      inputProps={{ "aria-label": "Without label" }}
                      value={link.type}
                      variant="standard"
                      label="Type"
                      onChange={(e) => {
                        const newLink = { ...link };
                        newLink.type = e.target.value;
                        const newLinkList = [...linkList];
                        newLinkList[index] = newLink;
                        setLinkList(newLinkList);
                      }}
                    >
                      <MenuItem value={"revolute"}>Revolute</MenuItem>
                      <MenuItem value={"prismatic"}>Prismatic</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right" style={{ padding: 5 }}>
                    <Checkbox
                      sx={{ padding: 0.5 }}
                      checked={!link.hideAxis}
                      onChange={(e) => {
                        const newLink = { ...link };
                        newLink.hideAxis = !e.target.checked;
                        const newLinkList = [...linkList];
                        newLinkList[index] = newLink;
                        setLinkList(newLinkList);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography
          style={{ marginTop: 20, marginBottom: 20, textAlign: "left" }}
        >
          End Effector Position:{" "}
          {`x: ${endEffectorPos.x.toFixed(2)}, y: ${endEffectorPos.y.toFixed(
            2
          )}, z: ${endEffectorPos.z.toFixed(2)}`}
        </Typography>
        <Button
          variant="contained"
          sx={{ margin: 1 }}
          onClick={() => {
            const newLink = {
              d: 10,
              theta: 0,
              a: 0,
              alpha: 0,
              type: "revolute",
            };
            const newLinkList = [...linkList, newLink];
            setLinkList(newLinkList);
          }}
        >
          Add Link
        </Button>
        <Button
          variant="contained"
          sx={{ margin: 1 }}
          onClick={() => {
            const newLinkList = [...linkList];
            newLinkList.pop();
            if (linkList.length > 1) setLinkList(newLinkList);
          }}
        >
          Remove Link
        </Button>
        <Button
          variant="contained"
          sx={{ margin: 1 }}
          onClick={() => {
            setStartAnimation(true);
          }}
        >
          Animation
        </Button>
      </div>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Typography style={{ textAlign: "left", marginRight: 20 }}>
          The unit of <Latex>$\theta_n$</Latex> and <Latex>$\alpha_n$</Latex>{" "}
          are
          <i> degree</i>.
          <br />
          The unit of <Latex>$d$</Latex> and <Latex>$a$</Latex> are <i>cm</i>.
        </Typography>
        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
          <InputLabel id="demo-simple-select-standard-label">
            Template
          </InputLabel>
          <Select
            labelId="demo-simple-select-standard-label"
            id="demo-simple-select-standard"
            value={age}
            onChange={handleChange}
            label="Age"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            <MenuItem value={"threeLinkPlanarArm"}>
              Three-link Planar Arm
            </MenuItem>
            <MenuItem value={"sphericalArm"}>Spherical Arm</MenuItem>
            <MenuItem value={"anthropomorphicArm"}>
              Anthropomorphic Arm
            </MenuItem>
            <MenuItem value={"sphericalWrist"}>Spherical Wrist</MenuItem>
            <MenuItem value={"stanfordManipulator"}>
              Standard Manipulator
            </MenuItem>
            <MenuItem value={"anthropomorphicArmSphericalWrist"}>
              Anthropomorphic Arm with Spherical Wrist
            </MenuItem>
            <MenuItem value={"dlrManipulator"}>DLR Manipulator</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <br />
      <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
        Revolute Joint :{" "}
        <Latex>
          $d, \alpha, a$ are set during the initial setup. $\theta$ changes
          during operation..
        </Latex>
        <br />
        Prismatic Joint :{" "}
        <Latex>
          $\theta, \alpha, a$ are set during the initial setup. $d$ changes
          during operation..
        </Latex>
      </Typography>
      <br />
      <br />
      <br />
      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        Here is notation of classic DH parameters
      </Typography>
      <img
        style={{ maxWidth: 400, marginBottom: 10 }}
        src={`https://upload.wikimedia.org/wikipedia/commons/8/85/Classic_DH_Parameters_Convention.png`}
        alt={"Standard DH parameters"}
        loading="lazy"
      />
      <Latex>
        The transformation matrix of classic DH parameters is:
        {`$$\{}^{n - 1}T_n = Trans_{z_{n-1}}(d_n) \\cdot Rot_{z_{n-1}}(\\theta_n)$$` +
          `$$\\cdot Trans_{x_{n}}(a_n) \\cdot Rot_{x_{n}}(\\alpha_n)$$`}
      </Latex>

      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        For more details, please check{" "}
        <a href="https://en.wikipedia.org/wiki/Denavit%E2%80%93Hartenberg_parameters">
          the DH parameters wiki
        </a>
        . I show the equation here because it is different from modified DH
        parameters and we will compare them later.
      </Typography>
      <NextAction
        label={"Next: Modified DH parameters"}
        to="/modified-dh-model"
      />
    </div>
  );
}
